/**
 * Which tour is currently running.
 *
 * The controller has to be mounted above the routes, since the tour navigates between
 * pages and would otherwise unmount itself. This is how a route component (or a
 * first-login hook later) tells it what to run.
 */

import { useEffect, useState } from 'react';
import type { TourDefinition } from './tour.types';

let active: TourDefinition | null = null;
const listeners = new Set<(t: TourDefinition | null) => void>();

function emit(): void {
  listeners.forEach((fn) => fn(active));
}

export function setActiveTour(tour: TourDefinition): void {
  active = tour;
  emit();
}

export function clearActiveTour(): void {
  active = null;
  emit();
}

export function getActiveTour(): TourDefinition | null {
  return active;
}

export function useActiveTour(): TourDefinition | null {
  const [tour, setTour] = useState<TourDefinition | null>(active);
  useEffect(() => {
    listeners.add(setTour);
    // Child effects run before parent ones, so a route component may already have set
    // the tour before this subscription existed. Re-sync instead of missing it.
    setTour(active);
    return () => {
      listeners.delete(setTour);
    };
  }, []);
  return tour;
}
